import { Metadata } from '@metaplex-foundation/mpl-token-metadata';
import { utils } from '@coral-xyz/anchor';
import { MintInfo, TOKEN_PROGRAM_ID, AccountLayout, u64 } from '@solana/spl-token';
import { Connection, PublicKey } from '@solana/web3.js';
import { unpackMint } from '@solana/spl-token-0.4';

const TOKEN_METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');

// This is not an official program but a community deployement
const TOKEN_2022_METADATA_PROGRAM_ID = new PublicKey('META4s4fSmpkTbZoUsgC1oBnWB31vQcmnN8giPw51Zu');

export function findMetadataAddress(mint: PublicKey, tokenProgramId: PublicKey): PublicKey {
  const metadataProgramId = tokenProgramId.equals(TOKEN_PROGRAM_ID)
    ? TOKEN_METADATA_PROGRAM_ID
    : TOKEN_2022_METADATA_PROGRAM_ID;

  return PublicKey.findProgramAddressSync(
    [Buffer.from('metadata'), metadataProgramId.toBuffer(), mint.toBuffer()],
    metadataProgramId,
  )[0];
}

type TokenMetadata = {
  publicKey: PublicKey;
  account: Metadata;
};

type TokenMintWithProgramId = {
  mint: PublicKey;
  programId: PublicKey;
};

interface RawMintWithProgramId extends MintInfo {
  programId: PublicKey;
}

export const TOKEN_2022_PROGRAM_ID = new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb');

export async function fetchMintInfos(
  connection: Connection,
  mints: PublicKey[],
): Promise<Array<[string, RawMintWithProgramId]>> {
  const results = (await utils.rpc.getMultipleAccounts(connection, mints)).reduce((acc, m, idx) => {
    if (m) {
      const { publicKey, account } = m;
      const isFromATokenProgram = account.owner.equals(TOKEN_PROGRAM_ID) || account.owner.equals(TOKEN_2022_PROGRAM_ID);

      if (isFromATokenProgram) {
        try {
          const mint = unpackMint(publicKey, account, account.owner);
          acc.push([
            mints[idx].toString(),
            {
              mintAuthority: mint.mintAuthority,
              supply: new u64(mint.supply.toString()),
              decimals: mint.decimals,
              isInitialized: mint.isInitialized,
              freezeAuthority: mint.freezeAuthority,
              programId: m.account.owner,
            },
          ]);
        } catch (error) {
          console.log('Failed to fetch or decode mint', mints[idx].toString(), error);
        }
      }
    }
    return acc;
  }, new Array<[string, RawMintWithProgramId]>());

  return results;
}

export const removeEmptyChars = (value: string) => value.replace(/\u0000/g, '');

export async function fetchTokenMetadatas(
  connection: Connection,
  tokens: TokenMintWithProgramId[],
): Promise<TokenMetadata[]> {
  const metadataAddresses = tokens.map((t) => findMetadataAddress(t.mint, t.programId));

  const tokenMetaAccounts = (await utils.rpc.getMultipleAccounts(connection, metadataAddresses)).reduce(
    (acc, keyedAccount) => {
      if (keyedAccount) {
        const metadata = Metadata.deserialize(keyedAccount.account.data)[0];
        metadata.data.name = removeEmptyChars(metadata.data.name);
        metadata.data.symbol = removeEmptyChars(metadata.data.symbol);
        metadata.data.uri = removeEmptyChars(metadata.data.uri);

        acc.push({
          publicKey: keyedAccount.publicKey,
          account: metadata,
        });
      }
      return acc;
    },
    new Array<TokenMetadata>(),
  );

  return tokenMetaAccounts;
}
